'use client';

import { areAnalyticsCookiesAllowed } from '@/lib/analytics/consent';

declare global {
  interface Window {
    fbq?: (
      command: string,
      eventName: string,
      params?: Record<string, unknown>,
      options?: { eventID: string },
    ) => void;
  }
}

export const META_COMPLETE_REGISTRATION_EVENT = {
  value: 200,
  currency: 'SEK',
  content_name: 'Account Signup',
} as const;

export const META_SUBSCRIBE_EVENT = {
  value: 1000,
  currency: 'SEK',
  content_name: 'Pro Plan Upgrade',
  predicted_ltv: 4000,
} as const;

function createFallbackEventId(): string {
  const cryptoSource = globalThis.crypto;
  if (cryptoSource?.getRandomValues) {
    const bytes = cryptoSource.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join('');

    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }

  return `meta-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function createMetaEventId(): string {
  const cryptoSource = globalThis.crypto;
  if (typeof cryptoSource?.randomUUID === 'function') {
    return cryptoSource.randomUUID();
  }

  return createFallbackEventId();
}

export function getMetaEventHeaders(
  eventIds: Record<string, string | undefined>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(eventIds).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
}

type QueuedMetaEvent = {
  eventName: string;
  params: Record<string, unknown>;
  options: { eventID: string } | undefined;
};

const metaEventQueue: QueuedMetaEvent[] = [];
let drainTimerId: ReturnType<typeof setInterval> | null = null;
let drainTimeoutId: ReturnType<typeof setTimeout> | null = null;

const DRAIN_INTERVAL_MS = 100;
const DRAIN_TIMEOUT_MS = 30_000;
const MAX_QUEUED_EVENTS = 50;

function stopDrainPolling() {
  if (drainTimerId !== null) {
    clearInterval(drainTimerId);
    drainTimerId = null;
  }

  if (drainTimeoutId !== null) {
    clearTimeout(drainTimeoutId);
    drainTimeoutId = null;
  }
}

function clearQueuedEvents() {
  metaEventQueue.length = 0;
  stopDrainPolling();
}

function drainQueue() {
  if (!areAnalyticsCookiesAllowed()) {
    clearQueuedEvents();
    return;
  }

  if (typeof window.fbq !== 'function') return;

  stopDrainPolling();

  while (metaEventQueue.length > 0) {
    const event = metaEventQueue.shift()!;
    window.fbq('track', event.eventName, event.params, event.options);
  }
}

function startDrainPolling() {
  if (drainTimerId !== null) return;

  drainTimerId = setInterval(drainQueue, DRAIN_INTERVAL_MS);
  drainTimeoutId = setTimeout(stopDrainPolling, DRAIN_TIMEOUT_MS);
}

export function trackMetaEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
) {
  if (typeof window === 'undefined') return;
  if (!areAnalyticsCookiesAllowed()) {
    clearQueuedEvents();
    return;
  }

  const options = eventId ? { eventID: eventId } : undefined;
  const resolvedParams = params ?? {};

  if (typeof window.fbq !== 'function') {
    if (metaEventQueue.length >= MAX_QUEUED_EVENTS) {
      metaEventQueue.shift();
    }

    metaEventQueue.push({ eventName, params: resolvedParams, options });
    startDrainPolling();
    return;
  }

  drainQueue();
  window.fbq('track', eventName, resolvedParams, options);
}
